import 'firebase/auth';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { jwtDecode } from "jwt-decode";
import { createContext, useContext } from 'react';

// Create the context with an initial value of null (before AuthProvider is used)
const AuthContext = createContext<AuthenticationService | null>(null);

// Export the context so it can be used elsewhere
export { AuthContext };

interface UserClaims {
    orgId: string;
    roles: string[];
    //[key: string]: string | boolean | undefined; // Allow additional properties
}

class AuthenticationService {

    private user: User | null = null;
    private claims: UserClaims | null = null;
    userListeners: ((user: User | null) => void)[] = [];

    private auth = getAuth();
    constructor() {
        this.initializeAuth();
    }

    private initializeAuth() {

        // Listen to auth state changes
        onAuthStateChanged(this.auth, async (user) => {
            if (user) {
                this.user = user;
                await this.fetchUserClaims();
            } else {
                this.user = null;
                this.claims = null;
            }

            this.userListeners.forEach((callback) => callback(this.user));
        });
    }

    onUserChange(callback: (claims: User | null) => void) {
        this.userListeners.push(callback);
    }

    // Fetch the custom claims from the ID token
    private async fetchUserClaims() {
        if (this.user) {
            try {
                const idToken = await this.user.getIdToken(false); // Force refresh token
                this.claims = jwtDecode(idToken); // Decode the token to get the claims
            } catch (error) {
                console.error('Error fetching user claims:', error);
                this.claims = null;
            }
        }
    }

    public getClaims(): UserClaims | null {
        return this.claims;
    }

    // Get the current user
    public getUser(): User | null {
        return this.user;
    }

    getRoles(): string[] {
        return this.claims?.roles ?? [];
    }

    // Get the current user ID
    public getUserId(): string {
        return this.user?.uid ?? "";
    }

    public getOrgId(): string {
        return this.claims?.orgId ?? "";
    }

    getCurrentUserName(): string | null {
        const user = this.auth.currentUser;
        return user ? user.displayName || user.email : null;
    }

    async logOut(): Promise<void> {
        try {
            await getAuth().signOut();
            console.log('User signed out successfully');
        } catch (error) {
            console.error('Error signing out: ', error);
        }
    }


}

export default AuthenticationService;

export function useAuthService(): AuthenticationService {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthService must be used within an AuthProvider');
    }
    return context;
}

// Provide the AuthenticationService in the component tree
export function AuthProvider({ children }: { children: React.ReactNode }) {
    const authService = new AuthenticationService();

    return <AuthContext.Provider value={authService}>{children}</AuthContext.Provider>;
}

const allRoles = {
    admin: 'Admin',
    editor: 'Editor',
    viewer: 'Viewer'
};

export { allRoles };